/**
 * Facebook Conversions API client-side utility
 * This service handles sending events to our Netlify function which forwards them to Facebook Conversions API
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Hash a string using SHA-256
 * @param {string} str - String to hash
 * @returns {Promise<string>} - Hashed string
 */
async function hashData(str) {
  if (!str) return '';

  try {
    // Encode as UTF-8
    const msgBuffer = new TextEncoder().encode(str.trim().toLowerCase());

    // Hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

    // Convert hash to hex string
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.error('Hashing error:', error);
    return '';
  }
}

/**
 * Prepare user data with proper hashing for privacy
 * @param {Object} userData - User data to prepare
 * @returns {Promise<Object>} - Prepared user data object
 */
/**
 * Get IP address using a public API
 * Note: This is not very reliable and only used as a fallback
 * @returns {Promise<string|null>} - IP address or null if not available
 */
async function getIPAddress() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return null;
  }
}

async function prepareUserData(userData = {}) {
  const { email, phone, firstName, lastName, city, country, zipCode, ...rest } = userData;

  // Always include these fields according to Facebook best practices
  const preparedData = {
    ...rest,
    client_user_agent: navigator.userAgent,
    client_ip_address: '', // This will be set by the server
  };

  // Try to get IP address client-side as a fallback
  // Facebook needs this for better matching
  try {
    const ipAddress = await getIPAddress();
    if (ipAddress) preparedData.client_ip_address = ipAddress;
  } catch (error) {
    console.log('Could not retrieve IP address client-side');
  }

  // Hash user PII data if available
  if (email) {
    preparedData.em = await hashData(email);
  }

  if (phone) preparedData.ph = await hashData(phone);
  if (firstName) preparedData.fn = await hashData(firstName);
  if (lastName) preparedData.ln = await hashData(lastName);
  if (city) preparedData.ct = await hashData(city);
  if (country) preparedData.country = await hashData(country);
  if (zipCode) preparedData.zp = await hashData(zipCode);

  // Generate a temporary external_id if we don't have a user ID
  // This helps maintain consistency across events
  if (userData.userId) {
    preparedData.external_id = await hashData(userData.userId);
  } else {
    // Try to use a consistent identifier across sessions
    let tempUserId = localStorage.getItem('fbcapi_temp_id');
    if (!tempUserId) {
      tempUserId = uuidv4();
      localStorage.setItem('fbcapi_temp_id', tempUserId);
    }
    preparedData.external_id = await hashData(tempUserId);
  }

  // Add fbp (Facebook Browser Pixel) if available
  const fbp = getFacebookBrowserPixelId();
  if (fbp) preparedData.fbp = fbp;

  // Add fbc (Facebook Click ID) if available
  const fbc = getFacebookClickId();
  if (fbc) preparedData.fbc = fbc;

  return preparedData;
}

/**
 * Get Facebook Browser Pixel ID from cookie if available
 * @returns {string|null} - The fbp value or null
 */
function getFacebookBrowserPixelId() {
  const cookies = document.cookie.split(';');
  const fbpCookie = cookies.find(cookie => cookie.trim().startsWith('_fbp='));
  return fbpCookie ? fbpCookie.split('=')[1] : null;
}

/**
 * Get Facebook Click ID from URL or cookies
 * @returns {string|null} - The fbc value or null
 */
function getFacebookClickId() {
  // First check URL for fbclid parameter
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');

  if (fbclid) {
    // Format: fb.1.{timestamp}.{fbclid}
    return `fb.1.${Date.now()}.${fbclid}`;
  }

  // If not in URL, check cookies
  const cookies = document.cookie.split(';');
  const fbcCookie = cookies.find(cookie => cookie.trim().startsWith('_fbc='));
  return fbcCookie ? fbcCookie.split('=')[1] : null;
}

/**
 * Encode phone number identifier to anonymize purpose
 * Takes the phone number and creates a generic contact identifier
 * Example: 0319300 → cntct_031_9300
 *
 * @param {string} phoneNumber - Phone number to encode
 * @param {string} context - Optional context (e.g., 'header', 'contact', 'footer')
 * @returns {string} - Encoded phone identifier
 */
function encodePhoneIdentifier(phoneNumber, context = '') {
  try {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return 'cntct_unknown';
    }

    // Clean phone number - remove spaces, dashes, parentheses, plus signs
    const cleanPhone = phoneNumber.replace(/[\s\-()+ ]/g, '');

    // Take first 3 and last 4 digits if phone is long enough
    let encodedPhone = cleanPhone;
    if (cleanPhone.length >= 7) {
      const firstPart = cleanPhone.substring(0, 3);
      const lastPart = cleanPhone.substring(cleanPhone.length - 4);
      encodedPhone = `${firstPart}_${lastPart}`;
    }

    // Add context if provided
    if (context && context.length > 0) {
      const cleanContext = context.toLowerCase().replace(/[^a-z0-9]/g, '');
      const shortContext = cleanContext.substring(0, 4);
      return `cntct_${shortContext}_${encodedPhone}`;
    }

    return `cntct_${encodedPhone}`;
  } catch (error) {
    console.error('Error encoding phone identifier:', error);
    return 'cntct_encoded';
  }
}

/**
 * Encode form name to anonymize medical information
 * Uses the same method as URL encoding but keeps underscores as separators
 * Example: formular_programare_coronografie → frm_prog_coro
 *
 * @param {string} formName - Form name to encode
 * @returns {string} - Encoded form name
 */
function encodeFormName(formName) {
  try {
    if (!formName || formName.trim() === '') {
      return 'frm_unknown';
    }

    // Convert to lowercase and split by underscores, spaces, dashes
    const cleanName = formName.toLowerCase();
    const words = cleanName.split(/[_\s\-]/);

    // Process each word - take first 3 or 4 characters
    const encodedWords = words.map(word => {
      // Keep words with 1-2 characters as-is
      if (word.length <= 2) {
        return word;
      }
      // Take first 4 characters for longer words
      // This matches "version 2" from the requirements
      return word.substring(0, 4);
    });

    // Join words with underscores
    return encodedWords.join('_');
  } catch (error) {
    console.error('Error encoding form name:', error);
    return 'frm_encoded';
  }
}

/**
 * Encode URL path to anonymize medical information
 * Takes the path and shortens each word to first 3 characters
 * Example: /proceduri/cardiologie-clinica/electrocardiograma-ecg-ekg/
 * Becomes: /pro-car-cli-ele-ecg-ekg/
 *
 * @param {string} url - Full URL or path to encode
 * @returns {string} - Encoded path
 */
function encodeUrlForFacebook(url) {
  try {
    // Extract path from URL (remove domain)
    let path = url;
    if (url.includes('://')) {
      const urlObj = new URL(url);
      path = urlObj.pathname;
    }

    // If homepage, return as-is
    if (path === '/' || path === '') {
      return 'homepage';
    }

    // Remove leading and trailing slashes, then split by slashes, dots, and dashes
    const cleanPath = path.replace(/^\/+|\/+$/g, '');

    // Split by slashes, dots, and dashes to get all words
    const words = cleanPath.split(/[\/.\-]/);

    // Process each word
    const encodedWords = words.map(word => {
      // Keep words with 1-2 characters as-is
      if (word.length <= 2) {
        return word;
      }
      // Take first 3 characters for longer words
      return word.substring(0, 3);
    });

    // Join all words with dashes and wrap with slashes
    return '/' + encodedWords.join('-') + '/';
  } catch (error) {
    console.error('Error encoding URL:', error);
    return '/encoded-url/';
  }
}

/**
 * Check if we're in development mode
 * @returns {boolean}
 */
const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
};

/**
 * Send event to our Netlify function
 * @param {string} eventName - Name of the event (e.g., 'Purchase', 'Lead')
 * @param {Object} customData - Additional event data
 * @param {Object} userData - User data for the event
 * @returns {Promise<Object>} - Response from the server
 */
export async function trackEvent(eventName, customData = {}, userData = {}) {
  if (typeof window === 'undefined') return null; // Skip during SSR

  try {
    // Generate a unique event ID
    const eventId = uuidv4();

    // Prepare user data with proper hashing
    const preparedUserData = await prepareUserData(userData);

    // Add event-specific required fields for Facebook
    // For certain events, Facebook requires specific field formats
    let enhancedCustomData = { ...customData };

    // Add recommended fields if not already present
    if (eventName === 'ViewContent' && !enhancedCustomData.currency) {
      enhancedCustomData.currency = 'RON';
      if (!enhancedCustomData.value && enhancedCustomData.value !== 0) {
        enhancedCustomData.value = 0;
      }
    }

    if (eventName === 'Lead' && !enhancedCustomData.currency) {
      enhancedCustomData.currency = 'RON';
      if (!enhancedCustomData.value && enhancedCustomData.value !== 0) {
        enhancedCustomData.value = 0;
      }
    }

    // Prepare event data
    // For PageView, Lead, ViewContent, and Contact events, encode the URL to anonymize medical information
    let sourceUrl = window.location.href;
    if (eventName === 'PageView' || eventName === 'Lead' || eventName === 'ViewContent' || eventName === 'Contact') {
      const currentPath = window.location.pathname;

      // Special case for main news section
      if (eventName === 'ViewContent' && (currentPath === '/noutati/' || currentPath === '/noutati')) {
        const urlObj = new URL(sourceUrl);
        sourceUrl = `${urlObj.protocol}//${urlObj.host}/noutati/`;
      } else {
        const encodedPath = encodeUrlForFacebook(sourceUrl);
        // Reconstruct URL with encoded path
        const urlObj = new URL(sourceUrl);
        if (encodedPath === 'homepage') {
          sourceUrl = `${urlObj.protocol}//${urlObj.host}/`;
        } else {
          sourceUrl = `${urlObj.protocol}//${urlObj.host}${encodedPath}`;
        }
      }
    }

    const eventData = {
      name: eventName,
      id: eventId,
      sourceUrl: sourceUrl,
      customData: enhancedCustomData,
    };

    // Log in development mode
    if (isDevelopment()) {
      console.group(`🔍 Facebook Conversions API: ${eventName}`);
      console.log('Event Data:', eventData);
      console.log('User Data:', preparedUserData);
      console.groupEnd();
    }

    // Send to our Netlify function
    const response = await fetch('/.netlify/functions/fb-conversions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventData,
        userData: preparedUserData,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error sending event to Conversions API:', result);

      // Show more detailed error in development
      if (isDevelopment() && result.error) {
        console.error('Facebook CAPI Error Details:', result.error);
        console.info('💡 Tip: Make sure you have set up the FACEBOOK_PIXEL_ID and FACEBOOK_API_ACCESS_TOKEN environment variables in your Netlify dashboard');
      }

      return { success: false, error: result };
    }

    // Log success in development
    if (isDevelopment()) {
      console.log(`✅ Successfully sent ${eventName} event to Facebook CAPI`);
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Failed to track event:', error);
    return { success: false, error };
  }
}

/**
 * Convenience methods for common events
 */
export const fbEvents = {
  // Standard events
  pageView: (userData = {}) => {
    const encodedUrl = encodeUrlForFacebook(window.location.href);
    const contentId = encodedUrl === 'homepage' ? 'homepage' : encodedUrl;

    return trackEvent('PageView', {
      currency: 'RON',
      value: 1,
      contents: [{ id: contentId }],
      quantity: 1,
      delivery_category: 'in_store',
    }, userData);
  },

  viewContent: (customData = {}, userData = {}) => {
    // Determine content ID based on URL path
    const currentPath = window.location.pathname;
    let contentId;

    // Check if we're on the main news section page (e.g., /noutati/)
    if (currentPath === '/noutati/' || currentPath === '/noutati') {
      contentId = 'noutati';
    } else {
      // For individual news pages, encode the URL
      contentId = encodeUrlForFacebook(window.location.href);
      if (contentId === 'homepage') {
        // If somehow it's detected as homepage, use encoded path
        contentId = encodeUrlForFacebook(currentPath);
      }
    }

    return trackEvent('ViewContent', {
      currency: 'RON',
      value: 2, // As specified: 2 for ViewContent
      contents: [{ id: contentId }],
      quantity: 1,
      delivery_category: 'in_store',
      ...customData, // Merge any custom data passed by the caller
    }, userData);
  },

  lead: (formName, userData = {}) => {
    // Encode form name to anonymize medical information
    const encodedFormName = encodeFormName(formName);

    // Encode the current URL
    const encodedUrl = encodeUrlForFacebook(window.location.href);

    return trackEvent('Lead', {
      currency: 'RON',
      value: 100, // As specified: 100 for Lead
      contents: [{ id: encodedFormName }],
      quantity: 1,
      delivery_category: 'in_store',
    }, userData);
  },

  contact: (phoneNumber, context = '', userData = {}) => {
    // Encode phone number to create generic contact identifier
    const encodedPhoneId = encodePhoneIdentifier(phoneNumber, context);

    return trackEvent('Contact', {
      currency: 'RON',
      value: 20, // As specified: 20 for Contact
      contents: [{ id: encodedPhoneId }],
      quantity: 1,
      delivery_category: 'in_store',
    }, userData);
  },

  schedule: (scheduleData, userData = {}) =>
    trackEvent('Schedule', scheduleData, userData),

  completeRegistration: (registrationData, userData = {}) =>
    trackEvent('CompleteRegistration', registrationData, userData),

  search: (searchData, userData = {}) =>
    trackEvent('Search', searchData, userData),

  // Custom healthcare events
  viewDoctor: (doctorData, userData = {}) =>
    trackEvent('ViewDoctor', doctorData, userData),

  viewTreatment: (treatmentData, userData = {}) =>
    trackEvent('ViewTreatment', treatmentData, userData),

  readArticle: (articleData, userData = {}) =>
    trackEvent('ReadArticle', articleData, userData),

  shareContent: (shareData, userData = {}) =>
    trackEvent('ShareContent', shareData, userData),
};
